import { useState } from 'react';
import { useInsights } from '../api/hooks';
import type { InsightDays, InsightStep, Insights, MemberTier } from '../api/types';
import { PageHeader } from '../app/Shell';
import { STAGE_LABEL } from '../app/labels';
import { Avatar, Badge, Button, Card, DataTable, Empty, Funnel, Icon, Meter, QueryView, Segmented, Stat, StatGrid, fmtInt, fmtPct, seriesColor } from '../ui';

const RANGES: ReadonlyArray<{ value: `${InsightDays}`; label: string }> = [
  { value: '7', label: '7 日' },
  { value: '30', label: '30 日' },
  { value: '90', label: '90 日' },
];

const STEP_LABEL: Record<InsightStep, string> = {
  app_opened: '打開 App',
  onboarding_completed: '完成新手設定',
  first_story_started: '開始第一個故事',
  story_completed: '完成故事',
  paywall_viewed: '看商店 / 付費牆',
  purchase_started: '按下購買',
  purchase_verified: '完成購買',
};

const TIERS: ReadonlyArray<{ key: MemberTier; label: string }> = [
  { key: 'pass', label: '月卡' },
  { key: 'plus', label: 'Plus' },
  { key: 'premium', label: 'Premium' },
];

/** Met stages in relationship order (stranger never counts: only met NPCs are listed). */
const STAGES = ['acquainted', 'familiar', 'trusted', 'close', 'dating', 'partner'] as const;

/** World event types that move ink coins; an unknown one shows as its key. */
const COIN_SOURCE: Record<string, string> = {
  'turn.resolved': '故事選擇',
  'episode.completed': '完成故事',
  'training.completed': '訓練 / 上課',
  'social.step': '社交 / 送禮',
  'day.advanced': '每日結算（會籍每日墨幣）',
  'room.completed': '多人房間（已移除）',
  'goal.claimed': '目標獎勵',
  'life.changed': '生活（工作、購物）',
  tip: '打賞創作者（已移除）',
  purchase: '商店購買',
  'admin.adjust': '後台調整',
};

/** Who plays how far, who pays, which NPCs players fall for, and where ink coins come from and go. */
export function InsightsPage() {
  const [days, setDays] = useState<InsightDays>(30);
  const q = useInsights(days);
  return (
    <>
      <PageHeader
        title="數據洞察"
        description="轉化漏斗、會員、角色人氣及墨幣經濟（只計正式環境，不計 sandbox）"
        actions={
          <>
            <Segmented label="時間範圍" value={`${days}`} options={RANGES} onChange={(v) => setDays(Number(v) as InsightDays)} />
            <Button onClick={() => void q.refetch()} busy={q.isFetching}>
              <Icon name="refresh" size={16} /> 重新整理
            </Button>
          </>
        }
      />
      <QueryView query={q} loading="正在計算…">
        {(d) => <InsightsBody d={d} />}
      </QueryView>
    </>
  );
}

function InsightsBody({ d }: { d: Insights }) {
  const step = (name: InsightStep) => d.funnel.find((f) => f.name === name)?.users ?? 0;
  const members = TIERS.map((t) => ({ ...t, store: d.members[t.key]?.store ?? 0, granted: d.members[t.key]?.granted ?? 0 }));
  const paying = members.reduce((n, m) => n + m.store, 0);
  const metTotal = Object.values(d.stages).reduce((n, v) => n + v, 0);
  return (
    <>
      <StatGrid>
        <Stat label="付費轉化" value={fmtPct(step('purchase_verified'), step('onboarding_completed'))} hint={`過去 ${d.range} 日：完成購買 ÷ 完成新手設定`} />
        <Stat label="付費牆轉化" value={fmtPct(step('purchase_verified'), step('paywall_viewed'))} hint="完成購買 ÷ 看過商店" />
        <Stat label="付費會員（現在）" value={fmtInt(paying)} hint={members.map((m) => `${m.label} ${fmtInt(m.store)}`).join(' · ')} />
        <Stat label="存檔" value={fmtInt(d.saves)} hint={`認識了 ${fmtInt(metTotal)} 段關係`} />
      </StatGrid>

      <Card title="轉化漏斗" sub={`過去 ${d.range} 日內做過每一步的玩家人數`}>
        {d.funnel.some((f) => f.users > 0) ? <Funnel steps={d.funnel.map((f) => ({ label: STEP_LABEL[f.name], value: f.users }))} format={fmtInt} /> : <Empty hint="玩家打開 App 後就會開始累積。">這段時間沒有漏斗事件。</Empty>}
      </Card>

      <Card title="會員（現在生效）" sub="購買 = App Store 或網站；贈送 = 後台或推廣" flush>
        <DataTable
          rows={members}
          rowKey={(r) => r.key}
          empty="沒有會員。"
          columns={[
            { key: 'tier', header: '級別', render: (r) => r.label },
            { key: 'store', header: '購買', align: 'right', render: (r) => fmtInt(r.store), sort: (r) => r.store },
            { key: 'granted', header: '贈送', align: 'right', render: (r) => (r.granted ? fmtInt(r.granted) : <span className="faint">—</span>), sort: (r) => r.granted },
          ]}
        />
      </Card>

      <Card title="關係階段分佈" sub="所有存檔中已認識的角色">
        <Meter ariaLabel="關係階段分佈" parts={STAGES.map((st, i) => ({ key: st, value: d.stages[st] ?? 0, color: seriesColor(i), label: STAGE_LABEL[st] }))} />
        <div className="row small" style={{ marginTop: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {STAGES.map((st, i) => (
            <span key={st} className="nowrap">
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: seriesColor(i), marginRight: 4 }} />
              {STAGE_LABEL[st]} {fmtInt(d.stages[st] ?? 0)}（{fmtPct(d.stages[st] ?? 0, metTotal)}）
            </span>
          ))}
        </div>
      </Card>

      <Card title="角色人氣" sub="按認識人數排序 · 交往 = 交往中或戀人" flush>
        <DataTable
          rows={d.npcs}
          rowKey={(r) => r.npcId}
          initialSort={{ key: 'met', dir: 'desc' }}
          pageSize={25}
          empty="還沒有玩家認識任何角色。"
          columns={[
            {
              key: 'name',
              header: '角色',
              render: (r) => (
                <span className="row nowrap">
                  <Avatar url={r.portraitUrl} name={r.name} size={28} /> {r.name} <code className="faint small">{r.npcId}</code>
                </span>
              ),
              sort: (r) => r.name,
            },
            { key: 'met', header: '認識', align: 'right', render: (r) => fmtInt(r.met), sort: (r) => r.met },
            { key: 'close', header: '親近', align: 'right', render: (r) => fmtInt(r.stages['close'] ?? 0), sort: (r) => r.stages['close'] ?? 0 },
            { key: 'dating', header: '交往', align: 'right', render: (r) => <Dating n={(r.stages['dating'] ?? 0) + (r.stages['partner'] ?? 0)} of={r.met} />, sort: (r) => (r.stages['dating'] ?? 0) + (r.stages['partner'] ?? 0) },
            { key: 'affection', header: '平均好感', align: 'right', render: (r) => Math.round(r.avgAffection), sort: (r) => r.avgAffection },
          ]}
        />
      </Card>

      <StatGrid>
        <Stat label="墨幣總存量" value={fmtInt(d.coins.total)} hint="所有存檔合計（現在）" />
        <Stat label="每個存檔中位數" value={fmtInt(d.coins.median)} hint={`頭 10% 有 ${fmtInt(d.coins.p90)} 以上 · 最多一位 ${fmtInt(d.coins.max)}`} />
        <Stat label={`過去 ${d.range} 日發出`} value={fmtInt(d.coins.earned)} />
        <Stat label={`過去 ${d.range} 日用掉`} value={fmtInt(d.coins.spent)} tone={d.coins.spent < d.coins.earned / 2 ? 'warn' : undefined} hint={d.coins.earned ? `用掉發出的 ${fmtPct(d.coins.spent, d.coins.earned)}` : undefined} />
      </StatGrid>

      <Card title="墨幣來源與去向" sub={`過去 ${d.range} 日`} flush>
        <DataTable
          rows={d.coins.byType}
          rowKey={(r) => `${r.type}:${r.direction}`}
          initialSort={{ key: 'coins', dir: 'desc' }}
          empty="這段時間沒有墨幣進出。"
          columns={[
            {
              key: 'type',
              header: '原因',
              render: (r) => (
                <span className="nowrap">
                  {COIN_SOURCE[r.type] ?? r.type} <code className="faint small">{r.type}</code>
                </span>
              ),
              sort: (r) => r.type,
            },
            { key: 'direction', header: '方向', render: (r) => (r.direction === 'in' ? <Badge tone="ok">發出</Badge> : <Badge tone="warn">用掉</Badge>), sort: (r) => r.direction },
            { key: 'coins', header: '墨幣', align: 'right', render: (r) => fmtInt(r.coins), sort: (r) => r.coins },
            { key: 'events', header: '次數', align: 'right', render: (r) => fmtInt(r.events), sort: (r) => r.events },
          ]}
        />
      </Card>
    </>
  );
}

function Dating({ n, of }: { n: number; of: number }) {
  if (!n) return <span className="faint">—</span>;
  return (
    <span className="nowrap">
      {fmtInt(n)} <span className="faint small">{fmtPct(n, of)}</span>
    </span>
  );
}
